import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { ApiCallError, apiFetch } from '../lib/api.ts';
import { dateTime } from '../lib/format.ts';
import { useUser } from '../hooks/useUser.ts';

type UserRole =
  | 'admin'
  | 'manager'
  | 'operator'
  | 'viewer'
  | 'superadmin'
  | 'director'
  | 'operation'
  | 'finance_manager'
  | 'logistics';

interface UserRow {
  id: number;
  email: string;
  name: string;
  /** Primary role. */
  role: UserRole;
  /** Every role held, including the primary one. */
  roles?: UserRole[];
  isActive: boolean;
  createdAt: string;
  lastLoginAt: string | null;
}

/** Assignable roles, in the order the pickers show them. */
const ASSIGNABLE: Array<{ value: UserRole; label: string }> = [
  { value: 'finance_manager', label: 'finance_manager' },
  { value: 'director', label: 'director' },
  { value: 'logistics', label: 'logistics' },
  { value: 'operation', label: 'operation' },
  { value: 'superadmin', label: 'superadmin' },
  { value: 'viewer', label: 'viewer (legacy)' },
  { value: 'operator', label: 'operator (legacy)' },
  { value: 'manager', label: 'manager (legacy)' },
  { value: 'admin', label: 'admin (legacy)' },
];

/**
 * Checkbox list of ADDITIONAL roles. The primary role is excluded — it is
 * always held, and showing it as an unticked box would imply it can be removed.
 */
function ExtraRolePicker({ primary, value, onChange, canSuperadmin }: {
  primary: UserRole;
  value: UserRole[];
  onChange: (next: UserRole[]) => void;
  canSuperadmin: boolean;
}) {
  const toggle = (r: UserRole) =>
    onChange(value.includes(r) ? value.filter((x) => x !== r) : [...value, r]);
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 12px' }}>
      {ASSIGNABLE.filter((o) => o.value !== primary)
        .filter((o) => o.value !== 'superadmin' || canSuperadmin)
        .map((o) => (
          <label key={o.value} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.85em' }}>
            <input type="checkbox" checked={value.includes(o.value)} onChange={() => toggle(o.value)} />
            {o.label}
          </label>
        ))}
    </div>
  );
}

interface ListResp {
  users: UserRow[];
}

export function AdminPage() {
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'users'],
    queryFn: () => apiFetch<ListResp>('/api/admin/users'),
  });

  return (
    <>
      <header className="main-header">
        <h1 className="main-title">Users &amp; access</h1>
        <span className="main-subtitle">
          {data ? `${data.users.length} accounts` : ''}
        </span>
      </header>

      <CreateUserPanel onCreated={() => qc.invalidateQueries({ queryKey: ['admin', 'users'] })} />

      {isLoading && <div className="loading">Loading…</div>}
      {error && <div className="error">{(error as Error).message}</div>}

      {data && (
        <section className="panel">
          <header className="panel-header">
            <h2 className="panel-title">All accounts</h2>
          </header>
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Status</th>
                <th>Created</th>
                <th>Last login</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {data.users.map((u) => (
                <UserRowControls key={u.id} user={u} onChanged={() => qc.invalidateQueries({ queryKey: ['admin', 'users'] })} />
              ))}
            </tbody>
          </table>
        </section>
      )}
    </>
  );
}

function CreateUserPanel({ onCreated }: { onCreated: () => void }) {
  const { user: me } = useUser();
  const isSuperadmin = me?.role === 'superadmin';
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<UserRole>('viewer');
  const [extraRoles, setExtraRoles] = useState<UserRole[]>([]);
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () =>
      apiFetch('/api/admin/users', {
        method: 'POST',
        body: JSON.stringify({ email, name, password, role, extraRoles }),
      }),
    onSuccess: () => {
      setEmail('');
      setName('');
      setPassword('');
      setRole('viewer');
      setOpen(false);
      onCreated();
    },
    onError: (err) => {
      if (err instanceof ApiCallError) setError(err.message);
      else setError(String(err));
    },
  });

  if (!open) {
    return (
      <section className="panel">
        <header className="panel-header">
          <h2 className="panel-title">Create user</h2>
          <button
            onClick={() => setOpen(true)}
            style={panelButtonStyle}
          >
            + New
          </button>
        </header>
        <p style={{ color: 'var(--text-muted)', margin: 0 }}>
          Admins create accounts manually. There is no self-signup. Password reset
          is also admin-driven (no email magic link in v1).
        </p>
      </section>
    );
  }

  return (
    <section className="panel">
      <header className="panel-header">
        <h2 className="panel-title">Create user</h2>
        <button onClick={() => setOpen(false)} style={panelButtonStyle}>
          Cancel
        </button>
      </header>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setError(null);
          mutation.mutate();
        }}
        style={{ display: 'grid', gap: '0.75rem', maxWidth: 400 }}
      >
        <label>
          <div style={labelStyle}>Email</div>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            style={{ width: '100%' }}
          />
        </label>
        <label>
          <div style={labelStyle}>Full name</div>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            style={{ width: '100%' }}
          />
        </label>
        <label>
          <div style={labelStyle}>Initial password</div>
          <input
            type="text"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={8}
            style={{ width: '100%' }}
          />
        </label>
        <label>
          <div style={labelStyle}>Role</div>
          <select
            value={role}
            onChange={(e) => setRole(e.target.value as UserRole)}
            style={{ width: '100%' }}
          >
            <option value="finance_manager">Finance manager — all data + stock counts, money visible, no admin internals</option>
            <option value="director">Director — all data, no admin internals</option>
            <option value="logistics">Logistics — RFID operations + stock counts only, no money</option>
            <option value="operation">Operation — SKU/DO/SR/PO/GRN, no money</option>
            {isSuperadmin && (
              <option value="superadmin">Superadmin — full access</option>
            )}
            <option value="viewer">Viewer (legacy) — financial read-only</option>
            <option value="operator">Operator (legacy) — same as operation</option>
            <option value="manager">Manager (legacy) — same as director</option>
            <option value="admin">Admin (legacy) — same as superadmin</option>
          </select>
        </label>
        <label>
          <div style={labelStyle}>Additional roles (optional)</div>
          <ExtraRolePicker primary={role} value={extraRoles} onChange={setExtraRoles} canSuperadmin={isSuperadmin} />
        </label>
        {error && <div className="error">{error}</div>}
        <button
          type="submit"
          disabled={mutation.isPending}
          style={{
            background: 'var(--accent)',
            color: 'var(--bg)',
            border: 'none',
            padding: '0.6rem',
            fontWeight: 600,
            cursor: 'pointer',
            opacity: mutation.isPending ? 0.6 : 1,
          }}
        >
          {mutation.isPending ? 'Creating…' : 'Create user'}
        </button>
      </form>
    </section>
  );
}

function UserRowControls({ user, onChanged }: { user: UserRow; onChanged: () => void }) {
  const { user: me } = useUser();
  const isSuperadmin = me?.role === 'superadmin';
  const [editing, setEditing] = useState(false);
  const [role, setRole] = useState(user.role);
  const [extraRoles, setExtraRoles] = useState<UserRole[]>(
    (user.roles ?? []).filter((r) => r !== user.role),
  );
  const [isActive, setIsActive] = useState(user.isActive);
  const [resettingPassword, setResettingPassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');

  const updateMut = useMutation({
    mutationFn: () =>
      apiFetch(`/api/admin/users/${user.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ role, isActive, extraRoles }),
      }),
    onSuccess: () => {
      setEditing(false);
      onChanged();
    },
  });

  const resetMut = useMutation({
    mutationFn: () =>
      apiFetch(`/api/admin/users/${user.id}/reset-password`, {
        method: 'POST',
        body: JSON.stringify({ newPassword }),
      }),
    onSuccess: () => {
      setResettingPassword(false);
      setNewPassword('');
    },
  });

  return (
    <tr>
      <td>{user.name}</td>
      <td className="mono">{user.email}</td>
      <td>
        {editing ? (
          <select value={role} onChange={(e) => setRole(e.target.value as UserRole)}>
            <option value="finance_manager">finance_manager</option>
            <option value="director">director</option>
            <option value="logistics">logistics</option>
            <option value="operation">operation</option>
            {(isSuperadmin || role === 'superadmin') && (
              <option value="superadmin">superadmin</option>
            )}
            <option value="viewer">viewer</option>
            <option value="operator">operator</option>
            <option value="manager">manager</option>
            <option value="admin">admin</option>
          </select>
        ) : (
          <span className="mono">
            {user.role}
            {(user.roles ?? []).filter((r) => r !== user.role).map((r) => (
              <span key={r} style={{ opacity: 0.7 }}> +{r}</span>
            ))}
          </span>
        )}
        {editing && (
          <div style={{ marginTop: 4 }}>
            <ExtraRolePicker primary={role} value={extraRoles} onChange={setExtraRoles} canSuperadmin={isSuperadmin} />
          </div>
        )}
      </td>
      <td>
        {editing ? (
          <label style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <input
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
              style={{ width: 'auto', minWidth: 0 }}
            />
            <span style={{ fontSize: 12 }}>Active</span>
          </label>
        ) : (
          <span className={`badge ${user.isActive ? 'badge-ok' : 'badge-cancel'}`}>
            {user.isActive ? 'active' : 'disabled'}
          </span>
        )}
      </td>
      <td className="mono" style={{ fontSize: 11 }}>
        {dateTime(user.createdAt)}
      </td>
      <td className="mono" style={{ fontSize: 11 }}>
        {user.lastLoginAt ? dateTime(user.lastLoginAt) : '—'}
      </td>
      <td>
        {editing ? (
          <div style={{ display: 'flex', gap: 4 }}>
            <button onClick={() => updateMut.mutate()} style={smallBtn}>Save</button>
            <button
              onClick={() => {
                setEditing(false);
                setRole(user.role);
                setExtraRoles((user.roles ?? []).filter((r) => r !== user.role));
                setIsActive(user.isActive);
              }}
              style={smallBtn}
            >
              Cancel
            </button>
          </div>
        ) : resettingPassword ? (
          <div style={{ display: 'flex', gap: 4 }}>
            <input
              type="text"
              placeholder="new password (≥8)"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              minLength={8}
              style={{ width: 160 }}
            />
            <button
              onClick={() => resetMut.mutate()}
              disabled={newPassword.length < 8 || resetMut.isPending}
              style={smallBtn}
            >
              Set
            </button>
            <button onClick={() => { setResettingPassword(false); setNewPassword(''); }} style={smallBtn}>
              Cancel
            </button>
          </div>
        ) : (
          <div style={{ display: 'flex', gap: 4 }}>
            <button onClick={() => setEditing(true)} style={smallBtn}>Edit</button>
            <button onClick={() => setResettingPassword(true)} style={smallBtn}>Reset pw</button>
          </div>
        )}
      </td>
    </tr>
  );
}

const labelStyle: React.CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: 10,
  letterSpacing: '0.1em',
  textTransform: 'uppercase',
  color: 'var(--text-muted)',
  marginBottom: 4,
};

const panelButtonStyle: React.CSSProperties = {
  background: 'var(--surface-2)',
  border: '1px solid var(--border)',
  color: 'var(--text)',
  padding: '0.4rem 0.75rem',
  cursor: 'pointer',
  fontSize: 12,
  borderRadius: 2,
};

const smallBtn: React.CSSProperties = {
  background: 'var(--surface-2)',
  border: '1px solid var(--border)',
  color: 'var(--text-muted)',
  padding: '0.3rem 0.6rem',
  cursor: 'pointer',
  fontSize: 11,
  borderRadius: 2,
};
